import { Link } from "wouter";
import HelioLogoMark, { scrollToPageTop } from "@/components/HelioLogoMark";
import { Globe, Mail, Shield, Award, CheckCircle2, Star } from "lucide-react";
import { COMPANY_ADDRESS, LEGAL_NAME, SCORE_NAME, SUPPORT_EMAIL, TRADING_NAME, WEBSITE, WEBSITE_URL } from "@/constants/company";
import SupportPhoneLink from "@/components/SupportPhoneLink";

const SCROLL_TO_TOP_PATHS = new Set([
  "/privacy",
  "/terms",
  "/cookies",
  "/about",
  "/faq",
  "/contact",
  "/methodology",
  "/blog",
]);

const PLATFORM_LINKS = [
  { label: "Get a quote", href: "/quote" },
  { label: "Insights", href: "/blog" },
  { label: "FAQ", href: "/faq" },
  // { label: "About Helio", href: "/about" },
  { label: `${SCORE_NAME} methodology`, href: "/methodology" },
  { label: `${TRADING_NAME} installation network`, href: "/installer/apply" },
  { label: "Contact", href: "/contact" },
  // { label: "Blog API", href: "/developers/blog-api" },
] as const;

const LEGAL_LINKS: ReadonlyArray<{ label: string; href: string; external?: boolean }> = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Service", href: "/terms" },
  { label: "Cookies", href: "/cookies" },
  { label: "MCS Certificate Installers", href: "https://mcscertified.com", external: true },
  { label: "RECC Code", href: "https://www.recc.org.uk", external: true },
];

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/powlo",
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/powlo.co",
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/powloenergy",
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  // {
  //   label: "TikTok",
  //   href: "https://www.tiktok.com/@heliopowered",
  //   icon: (
  //     <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
  //       <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z" />
  //     </svg>
  //   ),
  // },
  // {
  //   label: "X",
  //   href: "https://x.com/heliopowered",
  //   icon: (
  //     <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
  //       <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  //     </svg>
  //   ),
  // },
  // {
  //   label: "YouTube",
  //   href: "https://www.youtube.com/@heliopowered",
  //   icon: (
  //     <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
  //       <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  //     </svg>
  //   ),
  // },
] as const;

function FooterLink({
  href,
  label,
  external,
  linkClass,
}: {
  href: string;
  label: string;
  external?: boolean;
  linkClass: string;
}) {
  if (external || href.startsWith("mailto:") || href.startsWith("http") || href.startsWith("/#") || href.startsWith("/developers")) {
    return (
      <a
        href={href}
        className={linkClass}
        {...(external || href.startsWith("http")
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {label}
      </a>
    );
  }
  return (
    <Link
      href={href}
      className={linkClass}
      onClick={SCROLL_TO_TOP_PATHS.has(href) ? scrollToPageTop : undefined}
    >
      {label}
    </Link>
  );
}

export default function Footer({
  variant = "dark",
  showTrustpilot = true,
}: {
  variant?: "dark" | "light";
  showTrustpilot?: boolean;
}) {
  const isLight = variant === "light";

  const linkClass = isLight
    ? "text-sm text-[#666666] hover:text-[#0E9E77] active:text-[#0E9E77] transition-colors duration-200"
    : "text-sm text-white/40 hover:text-brand-lime active:text-brand-lime transition-colors duration-200";

  const muted = isLight ? "text-[#666666]" : "text-white/40";
  const mutedFaint = isLight ? "text-[#666666]/60" : "text-white/25";
  const heading = isLight ? "text-[#333333]" : "text-white/60";
  const divider = isLight ? "border-[rgba(17,17,17,0.08)]" : "border-white/8";
  const dividerSoft = isLight ? "border-[rgba(17,17,17,0.06)]" : "border-white/6";
  const badgeText = isLight ? "text-[#666666]" : "text-white/35";
  const badgeDot = isLight ? "text-[rgba(17,17,17,0.2)]" : "text-white/15";
  const copyright = isLight ? "text-[#666666]/70" : "text-white/20";

  return (
    <footer
      className={`border-t mt-auto ${divider} ${
        isLight ? "bg-[var(--home-bg)]" : "bg-[#050d1a]"
      }`}
    >
      <div className="container px-4 py-12 md:py-16">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2 sm:col-span-1">
            <HelioLogoMark className={isLight ? undefined : "text-brand-lime"} />
            {/* <p className={`text-xs mt-1 mb-3 ${mutedSoft}`}>by {LEGAL_NAME}</p> */}
            <p className={`text-xs leading-relaxed ${muted}`}>
              AI-powered solar assessments. {TRADING_NAME}-certified installation teams. Consumer protection
              standards and independent customer reviews.
            </p>
            <p className={`text-xs mt-3 leading-relaxed ${mutedFaint}`}>
              {COMPANY_ADDRESS.line1}, {COMPANY_ADDRESS.city}, {COMPANY_ADDRESS.postcode}
            </p>
          </div>

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${heading}`}>
              Platform
            </p>
            <div className="flex flex-col gap-2">
              {PLATFORM_LINKS.map((l) => (
                <FooterLink key={l.href} href={l.href} label={l.label} linkClass={linkClass} />
              ))}
            </div>
          </div>

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${heading}`}>
              Legal
            </p>
            <div className="flex flex-col gap-2">
              {LEGAL_LINKS.map((l) => (
                <FooterLink
                  key={l.href}
                  href={l.href}
                  label={l.label}
                  external={l.external}
                  linkClass={linkClass}
                />
              ))}
            </div>
          </div>

          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${heading}`}>
              Contact
            </p>
            <div className="flex flex-col gap-2">
              <a
                href={WEBSITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} flex items-center gap-1.5`}
              >
                <Globe className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {WEBSITE}
              </a>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className={`${linkClass} flex items-center gap-1.5`}
              >
                <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {SUPPORT_EMAIL}
              </a>
              <SupportPhoneLink
                className={`${linkClass} min-h-0`}
                iconClassName="h-3.5 w-3.5"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-8">
          <p className={`text-xs mr-1 ${isLight ? "text-[#666666]/70" : "text-white/30"}`}>
            Follow us
          </p>
          {SOCIAL_LINKS.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all ${
                isLight
                  ? "bg-white border border-[rgba(17,17,17,0.1)] text-[#666666] hover:text-[#0E9E77] hover:border-[#29ffa9]/50 hover:bg-[#29ffa9]/10"
                  : "bg-white/5 border border-white/8 text-white/40 hover:text-brand-lime hover:border-brand-lime/30 hover:bg-brand-lime/5"
              }`}
            >
              {s.icon}
            </a>
          ))}
        </div>

        <div
          className={`border-t pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 ${dividerSoft}`}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className={`flex items-center gap-1.5 text-xs ${badgeText}`}>
              <Shield className="h-3.5 w-3.5 text-[--color-green-deep]" aria-hidden /> MCS Certified Installers
            </span>
            <span className={badgeDot}>·</span>
            <span className={`flex items-center gap-1.5 text-xs ${badgeText}`}>
              <Award className="h-3.5 w-3.5 text-[--color-green-deep]" aria-hidden /> RECC Member
            </span>
            <span className={badgeDot}>·</span>
            <span className={`flex items-center gap-1.5 text-xs ${badgeText}`}>
              <CheckCircle2 className="h-3.5 w-3.5 text-[--color-green-deep]" aria-hidden /> TrustMark Approved
            </span>
            {showTrustpilot && (
              <>
                <span className={badgeDot}>·</span>
                <span className={`flex items-center gap-1.5 text-xs ${badgeText}`}>
                  <Star className="h-3.5 w-3.5 text-[--color-green-deep]" aria-hidden /> Trustpilot 4.9
                </span>
              </>
            )}
          </div>
          <p className={`text-xs ${copyright}`}>
            © {new Date().getFullYear()} {LEGAL_NAME}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
